import { z } from 'zod';

// Treat a blank string (e.g. `VITE_SENTRY_DSN=` in .env) the same as "not set":
// dotenv yields an empty string, which `.optional()` alone would still run through `.url()`.
const blankToUndefined = (v: unknown) => (v === '' ? undefined : v);

// The API base is normally an absolute URL, but same-origin proxy setups (e.g.
// the devtunnel `/gw` relay) point it at a root-relative path served from the
// same origin. Accept either.
const absoluteOrRootRelativeUrl = z
  .string()
  .refine((v) => v.startsWith('/') || /^https?:\/\//i.test(v), {
    message: 'must be an absolute URL or a root-relative path (starting with "/")',
  });

const schema = z.object({
  VITE_APP_ENV: z.enum(['local', 'dev', 'production']),
  VITE_API_BASE_URL: absoluteOrRootRelativeUrl,
  VITE_SENTRY_DSN: z.preprocess(blankToUndefined, z.string().url().optional()),
  // Direct download URL for the Clarix Tracker macOS .dmg (the SidebarRail's
  // "Tracker" button → TrackerOnboardingModal). Blank/unset hides the direct
  // download link; the onboarding flow itself still renders.
  VITE_TRACKER_DMG_URL: z.preprocess(blankToUndefined, z.string().url().optional()),
  // The apex/root host the app is served from (e.g. `clarix.sarvadhi.work`).
  // The workspace-slug parser strips this from the hostname instead of counting
  // labels, so it is correct for any label count (2-label `clarix.app`, 3-label
  // `clarix.sarvadhi.work`, …). Blank/unset → fall back to the `*.localhost`
  // dev convention (first label = slug).
  VITE_ROOT_DOMAIN: z.preprocess(blankToUndefined, z.string().optional()),
  // Optional local-dev override for the login method. When set, `loginMode()`
  // returns this regardless of the URL context (apex vs workspace subdomain),
  // so you can force OTP (or password) login without editing code. Blank/unset →
  // the default slug-based behavior.
  VITE_LOGIN_METHOD: z.preprocess(blankToUndefined, z.enum(['password', 'otp']).optional()),
  // Single-host mode: the whole app is served from ONE host with no per-workspace
  // subdomains (workspace is resolved from /me, not the hostname). When 'true',
  // getWorkspaceSlug returns null and workspace/apex URLs stay on the same host.
  // Blank/unset → false → the default subdomain (multi-host) behavior.
  VITE_SINGLE_HOST: z.preprocess(blankToUndefined, z.enum(['true', 'false']).optional()),
});

const parsed = schema.safeParse(import.meta.env);

if (!parsed.success) {
  const fieldErrors = parsed.error.flatten().fieldErrors;
  const detail = Object.entries(fieldErrors)
    .map(([key, messages]) => `${key}: ${messages?.join(', ')}`)
    .join('; ');
  console.error('❌ Invalid env:', fieldErrors);
  throw new Error(`Invalid environment configuration — ${detail}`);
}

export const env = parsed.data;

// The configured apex host (no protocol/port), or undefined when unset.
export const ROOT_DOMAIN = env.VITE_ROOT_DOMAIN;

// Local-dev login-method override: 'password' | 'otp' | undefined (default).
export const LOGIN_METHOD_OVERRIDE = env.VITE_LOGIN_METHOD;

// Single-host mode (no per-workspace subdomains). Default false → subdomain routing.
export const IS_SINGLE_HOST = env.VITE_SINGLE_HOST === 'true';

// Dev-build flag for dev-only tooling. Not `isLocal`: @tanstack/react-query-devtools
// no-ops unless NODE_ENV is development, so a `build:local` bundle would render the
// devtools chrome around a panel that can never draw.
export const IS_DEV = Boolean(import.meta.env.DEV);

export const isLocal = env.VITE_APP_ENV === 'local';
export const isDev = env.VITE_APP_ENV === 'dev';
export const isPrd = env.VITE_APP_ENV === 'production';

export type AppEnv = (typeof env)['VITE_APP_ENV'];

// App-identity constants. These do NOT belong in env — they describe the app, not the deployment.
export const APP_NAME = 'Clarix';
